import { InvoiceDiscountApplyToOption, InvoiceDiscountDraft } from "@components/invoice/create/components/InvoiceDiscountsCard";
import { InvoiceServiceFeeDraft } from "@components/invoice/create/components/InvoiceServiceFeesCard";
import { Add, Check, Close, DeleteOutline, EditOutlined } from "@mui/icons-material";
import { Box, Button, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useModals } from "../../../../../context/ModalContext";
import { BaseModalComponent } from "../../../../common/BaseModalComponent";
import { DiscountModalForm } from "../../../../invoice/create/components/DiscountModalForm";
import { ServiceFeeModalForm } from "../../../../invoice/create/components/ServiceFeeModalForm";
import { TFrequency } from "../../../../shared/components/features/event-scheduler/interfaces/event.interfaces";
import { invoiceEditorInnerBorder } from "../../../../ui/invoiceEditorSectionSurface";
import { recurringFee } from "../../../helpers/recurring-fee";
import { SalesResponse } from "../../../hooks/useGetSales";
import { ResettableInput } from "../../ResettableInput";
import { formatCurrency } from "./helpers";

interface TotalsSectionProps {
  salesData?: SalesResponse;
  /** Main service line total (user line); used when allowEdit instead of sales mainAreas.cost. */
  mainLineAmount: number;
  /** When set with allowEdit, a "Deposit credit" row is shown; Areas line shows gross (main + credit) for clarity. */
  depositCreditAmount?: number;
  /** Final amount to charge (respects global override). */
  total: number;
  /** Sum of row totals + fees before global override. */
  computedGrandTotal: number;
  isGlobalTotalOverridden: boolean;
  onGlobalTotalChange: (value: number | null) => void;
  onGlobalTotalReset: () => void;
  allowEdit: boolean;
  eventSchedulerFrequency?: TFrequency | null;
  recurringFeeValue: number;
  onRecurringFeeChange: (value: number | null) => void;
  serviceFeeLabel?: string;
  onServiceFeeLabelChange?: (label: string) => void;
  /** Optional extra line items total to show in invoice edit totals. */
  extraLineItemsAmount?: number;
  discountsAmount?: number;
  serviceFeesAmount?: number;
  hideServiceFee?: boolean;
  serviceFees?: InvoiceServiceFeeDraft[];
  serviceFeeApplyToOptions?: InvoiceDiscountApplyToOption[];
  serviceFeeComputedAmounts?: Record<string, number>;
  onAddServiceFee?: (draft?: Omit<InvoiceServiceFeeDraft, "id">) => void;
  onRemoveServiceFee?: (id: string) => void;
  onChangeServiceFee?: (id: string, patch: Partial<InvoiceServiceFeeDraft>) => void;
  showServiceFeeValidation?: boolean;
  discounts?: InvoiceDiscountDraft[];
  discountApplyToOptions?: InvoiceDiscountApplyToOption[];
  discountComputedAmounts?: Record<string, number>;
  onAddDiscount?: (draft?: Omit<InvoiceDiscountDraft, "id">) => void;
  onRemoveDiscount?: (id: string) => void;
  onChangeDiscount?: (id: string, patch: Partial<InvoiceDiscountDraft>) => void;
  showDiscountValidation?: boolean;
}

export const TotalsSection = ({
  salesData,
  mainLineAmount,
  depositCreditAmount,
  total,
  computedGrandTotal,
  isGlobalTotalOverridden,
  onGlobalTotalChange,
  onGlobalTotalReset,
  allowEdit,
  eventSchedulerFrequency,
  recurringFeeValue,
  onRecurringFeeChange,
  serviceFeeLabel = "Service fee",
  onServiceFeeLabelChange,
  extraLineItemsAmount = 0,
  discountsAmount = 0,
  serviceFeesAmount = 0,
  hideServiceFee = false,
  serviceFees,
  serviceFeeApplyToOptions,
  serviceFeeComputedAmounts,
  onAddServiceFee,
  onRemoveServiceFee,
  onChangeServiceFee,
  showServiceFeeValidation = false,
  discounts,
  discountApplyToOptions,
  discountComputedAmounts,
  onAddDiscount,
  onRemoveDiscount,
  onChangeDiscount,
  showDiscountValidation = false,
}: TotalsSectionProps) => {
  const { closeByKey } = useModals();
  const [isEditingLabel, setIsEditingLabel] = useState(false);
  const [labelDraft, setLabelDraft] = useState(serviceFeeLabel);

  useEffect(() => {
    if (!isEditingLabel) {
      setLabelDraft(serviceFeeLabel);
    }
  }, [serviceFeeLabel, isEditingLabel]);

  if (!salesData) return null;

  const showOtherAreasRow = salesData.otherAreas.cost > 0;
  const showAdditionalObjectsRow = salesData.additionalObjects.cost > 0;
  const showSpecialRequestsRow = salesData.specialRequests > 0;

  const extraLineItemsSafe = Number.isFinite(extraLineItemsAmount) ? Math.max(0, extraLineItemsAmount) : 0;
  const discountsSafe = Number.isFinite(discountsAmount) ? Math.max(0, discountsAmount) : 0;
  const serviceFeesSafe = Number.isFinite(serviceFeesAmount) ? Math.max(0, serviceFeesAmount) : 0;
  const displayGlobalTotal = total + extraLineItemsSafe + serviceFeesSafe - discountsSafe;
  const depositCredit = depositCreditAmount != null && depositCreditAmount > 0 ? depositCreditAmount : 0;
  const areasDisplayAmount = allowEdit && depositCredit > 0 ? mainLineAmount + depositCredit : allowEdit ? mainLineAmount : salesData.mainAreas.cost;

  // Invoice flow (allowEdit): always show the fee row so the user can type amounts without scheduler/sales calculations.
  const showRecurringFeeRow = !hideServiceFee && (allowEdit || (Boolean(eventSchedulerFrequency) && salesData.increasePercentageByFrequency != null));

  const handleGrandTotalChange = (value: number | null) => {
    if (value == null) return;
    // ListItemTable computes base grand total (service/deposit/fee) without extra line items.
    // Convert back from UI display total to base total before persisting manual override.
    onGlobalTotalChange(value - extraLineItemsSafe - serviceFeesSafe + discountsSafe);
  };

  return (
    <Box
      sx={{
        p: 3,
        bgcolor: "grey.50",
        borderTop: invoiceEditorInnerBorder,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
      }}
    >
      <Box sx={{ width: "100%", maxWidth: "420px", display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ px: 0.5 }}>
          <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.875rem" }}>
            {depositCredit > 0 ? "Areas in Service (before deposit)" : allowEdit ? "Subtotal (service items)" : "Areas in Service"}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: "medium", color: "text.secondary" }}>
            {formatCurrency(areasDisplayAmount)}
          </Typography>
        </Box>
        {showOtherAreasRow && (
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ px: 0.5 }}>
            <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.875rem" }}>
              Other Areas
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: "medium", color: "text.secondary" }}>
              {formatCurrency(salesData.otherAreas.cost)}
            </Typography>
          </Box>
        )}
        {showAdditionalObjectsRow && (
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ px: 0.5 }}>
            <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.875rem" }}>
              Additional Objects
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: "medium", color: "text.secondary" }}>
              {formatCurrency(salesData.additionalObjects.cost)}
            </Typography>
          </Box>
        )}
        {showSpecialRequestsRow && (
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ px: 0.5 }}>
            <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.875rem" }}>
              Special Requests
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: "medium", color: "text.secondary" }}>
              {formatCurrency(salesData.specialRequests)}
            </Typography>
          </Box>
        )}
        {extraLineItemsSafe > 0 && (
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ px: 0.5 }}>
            <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.875rem" }}>
              Extra line items
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: "text.secondary", fontSize: "0.875rem" }}>
              +{formatCurrency(extraLineItemsSafe)}
            </Typography>
          </Box>
        )}

        {/* Discounts rows, Edit and Add */}
        {discounts && discounts.length > 0 ? (
          discounts.map((discount) => {
            const discountAmount = discountComputedAmounts?.[discount.id] ?? 0;
            const applyOption = discountApplyToOptions?.find((opt) => opt.key === (discount.appliesToKey || "invoice"));
            const applyLabel = applyOption && applyOption.key !== "invoice" ? applyOption.label : null;
            const editModalKey = `edit-single-discount-${discount.id}`;

            return (
              <Box key={discount.id} display="flex" justifyContent="space-between" alignItems="center" sx={{ px: 0.5 }}>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.875rem" }}>
                    {discount.description || "Discount"}
                    {discount.kind === "percentage" ? ` (${discount.value}%)` : ""}
                    {applyLabel ? ` — ${applyLabel}` : ""}
                  </Typography>
                  <BaseModalComponent
                    modalKey={editModalKey}
                    modalLabel="Edit discount"
                    defaultSizes={{
                      width: 500,
                      height: 400,
                    }}
                    preventParentTrigger
                    allowInsidePreventModal
                    elementTrigger={
                      <Tooltip title="Edit discount">
                        <IconButton size="small" sx={{ p: 0.25, color: "action.active" }}>
                          <EditOutlined sx={{ fontSize: "0.95rem" }} />
                        </IconButton>
                      </Tooltip>
                    }
                  >
                    <DiscountModalForm
                      initialValues={discount}
                      applyToOptions={discountApplyToOptions}
                      onSave={(data) => {
                        onChangeDiscount?.(discount.id, data);
                        closeByKey(editModalKey);
                      }}
                      onCancel={() => closeByKey(editModalKey)}
                      isEdit
                    />
                  </BaseModalComponent>
                  <Tooltip title="Remove discount">
                    <IconButton
                      size="small"
                      onClick={() => onRemoveDiscount?.(discount.id)}
                      sx={{ p: 0.25, color: "action.active", "&:hover": { color: "error.main" } }}
                    >
                      <DeleteOutline sx={{ fontSize: "0.95rem" }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: "text.secondary", fontSize: "0.875rem" }}>
                  −{formatCurrency(discountAmount)}
                </Typography>
              </Box>
            );
          })
        ) : discountsSafe > 0 ? (
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ px: 0.5 }}>
            <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.875rem" }}>
              Discounts
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: "text.secondary", fontSize: "0.875rem" }}>
              −{formatCurrency(discountsSafe)}
            </Typography>
          </Box>
        ) : null}

        {allowEdit && depositCredit > 0 && (
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ px: 0.5 }}>
            <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.875rem" }}>
              Deposit credit (paid)
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: "success.dark", fontSize: "0.875rem" }}>
              −{formatCurrency(depositCredit)}
            </Typography>
          </Box>
        )}

        {/* Clean Service Fees rows with Modal Edit and Modal Add */}
        {serviceFees && serviceFees.length > 0 ? (
          serviceFees.map((fee) => {
            const feeAmount = serviceFeeComputedAmounts?.[fee.id] ?? 0;
            const applyOption = serviceFeeApplyToOptions?.find((opt) => opt.key === (fee.appliesToKey || "invoice"));
            const applyLabel = applyOption && applyOption.key !== "invoice" ? applyOption.label : null;
            const editModalKey = `edit-single-service-fee-${fee.id}`;

            return (
              <Box key={fee.id} display="flex" justifyContent="space-between" alignItems="center" sx={{ px: 0.5 }}>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.875rem" }}>
                    {fee.label || "Service fee"}
                    {fee.kind === "percentage" ? ` (${fee.value}%)` : ""}
                    {applyLabel ? ` — ${applyLabel}` : ""}
                  </Typography>
                  <BaseModalComponent
                    modalKey={editModalKey}
                    modalLabel="Edit service fee"
                    defaultSizes={{
                      width: 500,
                      height: 430,
                    }}
                    preventParentTrigger
                    allowInsidePreventModal
                    elementTrigger={
                      <Tooltip title="Edit fee">
                        <IconButton size="small" sx={{ p: 0.25, color: "action.active" }}>
                          <EditOutlined sx={{ fontSize: "0.95rem" }} />
                        </IconButton>
                      </Tooltip>
                    }
                  >
                    <ServiceFeeModalForm
                      initialValues={fee}
                      applyToOptions={serviceFeeApplyToOptions}
                      onSave={(data) => {
                        onChangeServiceFee?.(fee.id, data);
                        closeByKey(editModalKey);
                      }}
                      onCancel={() => closeByKey(editModalKey)}
                      isEdit
                    />
                  </BaseModalComponent>
                  <Tooltip title="Remove fee">
                    <IconButton
                      size="small"
                      onClick={() => onRemoveServiceFee?.(fee.id)}
                      sx={{ p: 0.25, color: "action.active", "&:hover": { color: "error.main" } }}
                    >
                      <DeleteOutline sx={{ fontSize: "0.95rem" }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: "text.secondary", fontSize: "0.875rem" }}>
                  +{formatCurrency(feeAmount)}
                </Typography>
              </Box>
            );
          })
        ) : serviceFeesSafe > 0 ? (
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ px: 0.5 }}>
            <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.875rem" }}>
              Service fees
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: "text.secondary", fontSize: "0.875rem" }}>
              +{formatCurrency(serviceFeesSafe)}
            </Typography>
          </Box>
        ) : null}

        {/* Action buttons: Add Discount & Add Service Fee */}
        {(onAddDiscount || onAddServiceFee) && (
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2, px: 0.5, mt: -0.5 }}>
            {onAddDiscount && (
              <BaseModalComponent
                modalKey="add-single-invoice-discount"
                modalLabel="Add discount"
                defaultSizes={{
                  width: 500,
                  height: 400,
                }}
                preventParentTrigger
                allowInsidePreventModal
                elementTrigger={
                  <Button
                    size="small"
                    startIcon={<Add sx={{ fontSize: 16 }} />}
                    sx={{ textTransform: "none", fontSize: "0.8125rem", fontWeight: 700, p: 0, minWidth: 0 }}
                  >
                    Add discount
                  </Button>
                }
              >
                <DiscountModalForm
                  applyToOptions={discountApplyToOptions}
                  onSave={(data) => {
                    onAddDiscount?.(data);
                    closeByKey("add-single-invoice-discount");
                  }}
                  onCancel={() => closeByKey("add-single-invoice-discount")}
                />
              </BaseModalComponent>
            )}

            {onAddServiceFee && (
              <BaseModalComponent
                modalKey="add-single-invoice-service-fee"
                modalLabel="Add service fee"
                defaultSizes={{
                  width: 500,
                  height: 430,
                }}
                preventParentTrigger
                allowInsidePreventModal
                elementTrigger={
                  <Button
                    size="small"
                    startIcon={<Add sx={{ fontSize: 16 }} />}
                    sx={{ textTransform: "none", fontSize: "0.8125rem", fontWeight: 700, p: 0, minWidth: 0 }}
                  >
                    Add service fee
                  </Button>
                }
              >
                <ServiceFeeModalForm
                  applyToOptions={serviceFeeApplyToOptions}
                  onSave={(data) => {
                    onAddServiceFee?.(data);
                    closeByKey("add-single-invoice-service-fee");
                  }}
                  onCancel={() => closeByKey("add-single-invoice-service-fee")}
                />
              </BaseModalComponent>
            )}
          </Box>
        )}

        {!serviceFees && showRecurringFeeRow && (
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ px: 0.5 }}>
            {allowEdit ? (
              isEditingLabel ? (
                <Box display="flex" alignItems="center" gap={0.5}>
                  <TextField
                    size="small"
                    value={labelDraft}
                    onChange={(e) => setLabelDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        const trimmed = labelDraft.trim() || "Service fee";
                        onServiceFeeLabelChange?.(trimmed);
                        setIsEditingLabel(false);
                      } else if (e.key === "Escape") {
                        setIsEditingLabel(false);
                      }
                    }}
                    autoFocus
                    sx={{
                      width: "140px",
                      "& .MuiInputBase-input": {
                        fontSize: "0.875rem",
                        py: 0.25,
                        px: 0.75,
                      },
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => {
                      const trimmed = labelDraft.trim() || "Service fee";
                      onServiceFeeLabelChange?.(trimmed);
                      setIsEditingLabel(false);
                    }}
                    sx={{ p: 0.25, color: "success.main" }}
                  >
                    <Check sx={{ fontSize: "1rem" }} />
                  </IconButton>
                  <IconButton size="small" onClick={() => setIsEditingLabel(false)} sx={{ p: 0.25, color: "text.secondary" }}>
                    <Close sx={{ fontSize: "1rem" }} />
                  </IconButton>
                </Box>
              ) : (
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.875rem" }}>
                    {serviceFeeLabel}
                  </Typography>
                  {onServiceFeeLabelChange && (
                    <Tooltip title="Edit label">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setLabelDraft(serviceFeeLabel);
                          setIsEditingLabel(true);
                        }}
                        sx={{ p: 0.25, color: "action.active" }}
                      >
                        <EditOutlined sx={{ fontSize: "1rem" }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              )
            ) : (
              <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.875rem" }}>
                Recurring Service Fee:{" "}
                <Box component="span" sx={{ fontWeight: "bold" }}>
                  {eventSchedulerFrequency ? recurringFee(eventSchedulerFrequency).freqencyFormatted : "—"}
                </Box>
              </Typography>
            )}
            <ResettableInput
              defaultValue={0}
              value={recurringFeeValue}
              onChange={allowEdit ? onRecurringFeeChange : undefined}
              readOnly={!allowEdit}
              formatValue={formatCurrency}
              width="100px"
              fontSize="0.875rem"
              fontWeight={500}
              align="right"
              showResetButton={false}
            />
          </Box>
        )}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          sx={{
            pt: 2,
            mt: salesData ? 0 : 0,
            borderTop: "1px solid",
            borderColor: "divider",
            px: 0.5,
          }}
        >
          <Box sx={{ pt: 0.5 }}>
            <Typography variant="h6" sx={{ fontWeight: "bold", color: "text.primary" }}>
              Total
            </Typography>
            {isGlobalTotalOverridden && (
              <Typography variant="caption" sx={{ fontStyle: "italic", color: "text.secondary", display: "block" }}>
                Adjusted
              </Typography>
            )}
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.25, minWidth: 140 }}>
            {allowEdit ? (
              <ResettableInput
                defaultValue={computedGrandTotal + extraLineItemsSafe + serviceFeesSafe - discountsSafe}
                value={displayGlobalTotal}
                onChange={handleGrandTotalChange}
                onReset={onGlobalTotalReset}
                readOnly={false}
                formatValue={formatCurrency}
                width="140px"
                fontSize="1.25rem"
                fontWeight={700}
                align="right"
                showResetButton
                resetTooltip="Restore total from line items (subtotal + fees + extras)"
              />
            ) : (
              <Typography variant="h6" sx={{ fontWeight: "bold", color: "text.primary" }}>
                {formatCurrency(displayGlobalTotal)}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
